import { DetailsList, SelectionMode } from '@fluentui/react';
import {
  Button,
  Dropdown,
  Field,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Option,
  Text,
  makeStyles,
} from '@fluentui/react-components';
import {
  AddRegular,
  CheckmarkRegular,
  DeleteRegular,
  DismissRegular,
  EditRegular,
} from '@fluentui/react-icons';
import { useState } from 'react';
import {
  IMetadata,
  IValidationRule as IValidationRuleEntry,
} from '../../services/metadataService';

export interface IValidationEditorProps {
  fieldName: string;
  fieldType?: string;
  rules: IValidationRuleEntry[];
  hasValidation: boolean;
  onRulesChange: (rules: IValidationRuleEntry[]) => void;
  onClear: () => void;
  metadata?: IMetadata;
}

interface IParsedValidationRule {
  type: string;
  value: string;
}

interface IValidationRuleOption {
  key: string;
  text: string;
}

interface ISpecialValueOption {
  key: string;
  text: string;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
  },
  headerRow: {
    alignItems: 'center',
    display: 'flex',
    gap: '12px',
    justifyContent: 'space-between',
  },
  fieldRow: {
    alignItems: 'flex-end',
    display: 'flex',
    gap: '12px',
    width: '100%',
  },
  fieldInput: {
    flex: 1,
  },
  actionRow: {
    alignItems: 'center',
    display: 'flex',
    gap: '8px',
  },
  ruleEditorRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
  },
  ruleEditorTopRow: {
    alignItems: 'flex-end',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    width: '100%',
  },
  ruleEditorBottomRow: {
    alignItems: 'flex-end',
    display: 'flex',
    gap: '12px',
    width: '100%',
  },
  ruleTypeField: {
    minWidth: '170px',
  },
  ruleValueField: {
    width: '120px',
  },
  ruleErrorField: {
    flex: 1,
    minWidth: '220px',
  },
  detailsActions: {
    display: 'flex',
    gap: '8px',
  },
});

const validationRuleToDisplay = (
  rule: IParsedValidationRule,
  metadata?: IMetadata,
  fieldName?: string
): string => {
  const field =
    metadata && fieldName
      ? metadata?.fields.find((f) => f.name === fieldName)
      : undefined;
  switch (rule.type) {
    case 'NE':
      return 'Not Empty';
    case 'NGT':
      return field?.fieldType === 'String'
        ? `Not longer than ${rule.value}`
        : `Not greater than ${rule.value}`;
    case 'NLT':
      return field?.fieldType === 'String'
        ? `Not shorter than ${rule.value}`
        : `Not less than ${rule.value}`;
    case 'RGX':
      return `Should match with ${rule.value}`;
    default:
      return rule.type;
  }
};

const stringToValidationRule = (rule: unknown): IParsedValidationRule => {
  const safeRule = typeof rule === 'string' ? rule : '';
  return { type: safeRule.substring(0, 3), value: safeRule.substring(3) };
};

const toEditableValidationRule = (
  rule: IParsedValidationRule
): IParsedValidationRule => {
  switch (rule.type) {
    case 'NGT':
      return { type: 'NG', value: rule.value };
    case 'NLT':
      return { type: 'NL', value: rule.value };
    case 'RGX':
      return { type: 'RX', value: rule.value };
    default:
      return rule;
  }
};

const toValidationRule = (ruleType: string, ruleValue: string): string => {
  return ruleType === 'NE'
    ? ruleType
    : ruleType === 'NG'
    ? `NGT${ruleValue}`
    : ruleType === 'NL'
    ? `NLT${ruleValue}`
    : ruleType === 'RX'
    ? `RGX${ruleValue}`
    : '';
};

const STRING_OPTIONS: IValidationRuleOption[] = [
  { key: 'NE', text: 'Not empty' },
  { key: 'NG', text: 'Not longer than' },
  { key: 'NL', text: 'Not shorter than' },
  { key: 'RX', text: 'Regex match' },
];

const NUMBER_OPTIONS: IValidationRuleOption[] = [
  { key: 'NE', text: 'Not empty' },
  { key: 'NG', text: 'Not greater than' },
  { key: 'NL', text: 'Not less than' },
];

const NUMBER_SPECIAL_VALUE_OPTIONS: ISpecialValueOption[] = [
  { key: 'currentYear', text: 'Current Year (currentYear)' },
];

const DATE_SPECIAL_VALUE_OPTIONS: ISpecialValueOption[] = [
  { key: 'currentDate', text: 'Current Date (currentDate)' },
];

const getOptions = (
  metadata?: IMetadata,
  fieldName?: string,
  fieldTypeOverride?: string
): IValidationRuleOption[] => {
  if (fieldName === 'code' || fieldName === 'name') return STRING_OPTIONS;
  const fieldType =
    fieldTypeOverride ??
    metadata?.fields.find((f) => f.name === fieldName)?.fieldType;
  if (fieldType === 'Number' || fieldType === 'Date') return NUMBER_OPTIONS;
  // Default to String options when field type is unknown or field not yet filled in
  return STRING_OPTIONS;
};

const getSpecialValueOptions = (
  metadata?: IMetadata,
  fieldName?: string,
  fieldTypeOverride?: string
): ISpecialValueOption[] => {
  const fieldType =
    fieldTypeOverride ??
    metadata?.fields.find((f) => f.name === fieldName)?.fieldType;

  if (fieldType === 'Number') {
    return NUMBER_SPECIAL_VALUE_OPTIONS;
  }

  if (fieldType === 'Date') {
    return DATE_SPECIAL_VALUE_OPTIONS;
  }

  return [];
};

const ValidationEditor = (props: IValidationEditorProps) => {
  const styles = useStyles();
  const localField = props.fieldName;
  const localRules = props.rules;

  // Inline rule editor (draft state — only moves to localRules on tick)
  const [ruleEditorMode, setRuleEditorMode] = useState<
    'add' | 'edit' | undefined
  >();
  const [editingRuleIndex, setEditingRuleIndex] = useState<
    number | undefined
  >();
  const [draftRule, setDraftRule] = useState<IParsedValidationRule>();
  const [draftErrorMessage, setDraftErrorMessage] = useState<string>('');

  interface IValidationRuleWithIndex {
    index: number;
    parsedRule: IParsedValidationRule;
    validationRule: IValidationRuleEntry;
  }

  const ruleOptions = getOptions(props.metadata, localField, props.fieldType);
  const specialValueOptions = getSpecialValueOptions(
    props.metadata,
    localField,
    props.fieldType
  );

  // Inline rule editor helpers — only mutate localRules
  const resetRuleEditor = () => {
    setRuleEditorMode(undefined);
    setEditingRuleIndex(undefined);
    setDraftRule(undefined);
    setDraftErrorMessage('');
  };

  const openAddRuleEditor = () => {
    setRuleEditorMode('add');
    setDraftRule({ type: ruleOptions[0]?.key ?? '', value: '' });
    setDraftErrorMessage('');
    setEditingRuleIndex(undefined);
  };

  const commitDraftRule = () => {
    if (!draftRule) return;
    const ruleEntry: IValidationRuleEntry = {
      rule: toValidationRule(draftRule.type, draftRule.value),
      errorMessage: draftErrorMessage,
    };
    if (ruleEditorMode === 'add') {
      props.onRulesChange([...localRules, ruleEntry]);
    } else if (ruleEditorMode === 'edit' && editingRuleIndex !== undefined) {
      props.onRulesChange(
        localRules.map((r, i) => (i === editingRuleIndex ? ruleEntry : r))
      );
    }
    resetRuleEditor();
  };

  const deleteLocalRule = (ruleIndex: number) => {
    props.onRulesChange(localRules.filter((_, i) => i !== ruleIndex));
  };

  return (
    <div className={styles.root}>
      <div className={styles.headerRow}>
        <Text size={600} weight="semibold">
          {localField || 'Validation rules'}
        </Text>
        <div className={styles.actionRow}>
          <Button
            appearance="subtle"
            icon={<DeleteRegular />}
            disabled={!props.hasValidation}
            onClick={props.onClear}
          >
            Remove validation
          </Button>
          <Button
            appearance="subtle"
            icon={<AddRegular />}
            disabled={!localField}
            onClick={openAddRuleEditor}
          >
            Add rule
          </Button>
        </div>
      </div>

      <DetailsList
        items={localRules.map((r, i) => ({
          parsedRule: stringToValidationRule(r.rule),
          validationRule: r,
          index: i,
        }))}
        selectionMode={SelectionMode.none}
        columns={[
          {
            key: 'rule',
            name: 'Rule',
            fieldName: 'rule',
            minWidth: 150,
            onRender: (item: IValidationRuleWithIndex) => (
              <span>
                {validationRuleToDisplay(
                  item.parsedRule,
                  props.metadata,
                  localField
                )}
              </span>
            ),
          },
          {
            key: 'errorMessage',
            name: 'Error message',
            fieldName: 'errorMessage',
            minWidth: 200,
            onRender: (item: IValidationRuleWithIndex) => (
              <span>{item.validationRule.errorMessage}</span>
            ),
          },
          {
            key: 'action',
            name: 'Action',
            minWidth: 80,
            onRender: (item: IValidationRuleWithIndex) => (
              <div className={styles.detailsActions}>
                <Button
                  appearance="subtle"
                  icon={<EditRegular />}
                  onClick={() => {
                    setRuleEditorMode('edit');
                    setDraftRule(toEditableValidationRule(item.parsedRule));
                    setDraftErrorMessage(item.validationRule.errorMessage);
                    setEditingRuleIndex(item.index);
                  }}
                />
                <Button
                  appearance="subtle"
                  icon={<DeleteRegular />}
                  onClick={() => deleteLocalRule(item.index)}
                />
              </div>
            ),
          },
        ]}
      />

      {ruleEditorMode ? (
        <div className={styles.ruleEditorRow}>
          <div className={styles.ruleEditorTopRow}>
            <Field className={styles.ruleTypeField} label="Rule">
              <Dropdown
                placeholder="Select a rule"
                value={
                  draftRule?.type
                    ? ruleOptions.find((o) => o.key === draftRule.type)?.text
                    : undefined
                }
                selectedOptions={draftRule?.type ? [draftRule.type] : []}
                onOptionSelect={(_event, data) =>
                  setDraftRule({
                    type: (data.optionValue ?? '').toString(),
                    value: draftRule?.value ?? '',
                  })
                }
              >
                {ruleOptions.map((option) => (
                  <Option key={option.key} value={option.key}>
                    {option.text}
                  </Option>
                ))}
              </Dropdown>
            </Field>

            {draftRule?.type !== 'NE' ? (
              <Field className={styles.ruleValueField} label="Value">
                <Input
                  value={draftRule?.value ?? ''}
                  onChange={(_event, data) =>
                    setDraftRule({
                      type: draftRule?.type ?? '',
                      value: data.value,
                    })
                  }
                  contentAfter={
                    specialValueOptions.length > 0 ? (
                      <Menu>
                        <MenuTrigger disableButtonEnhancement>
                          <Button appearance="subtle" size="small">
                            Special
                          </Button>
                        </MenuTrigger>
                        <MenuPopover>
                          <MenuList>
                            {specialValueOptions.map((option) => (
                              <MenuItem
                                key={option.key}
                                onClick={() =>
                                  setDraftRule({
                                    type: draftRule?.type ?? '',
                                    value: option.key,
                                  })
                                }
                              >
                                {option.text}
                              </MenuItem>
                            ))}
                          </MenuList>
                        </MenuPopover>
                      </Menu>
                    ) : undefined
                  }
                />
              </Field>
            ) : null}
          </div>

          <div className={styles.ruleEditorBottomRow}>
            <Field className={styles.ruleErrorField} label="Error message">
              <Input
                value={draftErrorMessage}
                onChange={(_event, data) => setDraftErrorMessage(data.value)}
              />
            </Field>

            <div className={styles.actionRow}>
              <Button
                appearance="subtle"
                icon={<CheckmarkRegular />}
                title="Confirm rule"
                onClick={commitDraftRule}
              />
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                title="Discard rule"
                onClick={resetRuleEditor}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ValidationEditor;
